import dayjs from 'dayjs'
import { getIn } from 'formik'
import { getQueryData } from '../../../api/helpers'
import { formatProjectAsyncOption } from '../../../api/projects/formatters'
import projects from '../../../api/projects/hooks'
import { formatPropertyAsyncOption } from '../../../api/properties/formatters'
import properties from '../../../api/properties/hooks'
import { syncFormikToEvent } from '../../../Components/Modals/ActivityModal/utils'
import i18n from '../../../i18n'
import { store } from '../../../store'
import { updateModal } from '../../../store/modal'
import { MODAL_TYPES } from '../../constants'
import {
    baseAsyncSelectInput,
    baseCheckboxInput,
    baseDateInput,
    baseSelectInput,
    baseTextAreaInput,
    baseTextInput,
    baseTimeInput,
} from '../fieldTypes'
import { activityFields } from './fields'

export const activityFieldTypes = () => {
    return {
        [activityFields.ID]: {
            ...baseTextInput,
            name: activityFields.ID,
            label: i18n.t('modal.activity.field.activityId'),
            disabled: true,
            colProps: {
                sm: 12,
            },
        },
        [activityFields.TITLE]: {
            ...baseTextInput,
            name: activityFields.TITLE,
            label: i18n.t('app.common.name'),
            required: true,
            colProps: {
                sm: 12,
            },
        },
        [activityFields.ACTIVITY_TYPE_ID]: {
            ...baseSelectInput,
            name: activityFields.ACTIVITY_TYPE_ID,
            label: i18n.t('app.common.activityType'),
            required: true,
            colProps: {
                sm: 12,
            },
            handleChange: (value, formik) => {
                const state = store.getState()
                const activityValues = getQueryData(['activityValues'])
                const activityTypes = activityValues?.type ?? []
                const selectedType = activityTypes.find((type) => type.id === Number(value))

                if (selectedType) {
                    const currentModalProps = state.modal.modalProps[MODAL_TYPES.ACTIVITY_MODAL]

                    // Preserves user input during formik reinitialization (activity type switch)
                    const updatedEvent = syncFormikToEvent(formik.values, currentModalProps?.event || {}, selectedType)

                    store.dispatch(
                        updateModal({
                            modalType: MODAL_TYPES.ACTIVITY_MODAL,
                            modalProps: {
                                event: updatedEvent,
                            },
                        })
                    )
                }
            },
        },
        [activityFields.DATE_FROM]: {
            ...baseDateInput,
            name: activityFields.DATE_FROM,
            colProps: {
                sm: 7,
                className: 'pe-0',
            },
            handleChange: (value, formik) => {
                const dateToValue = getIn(formik.values, activityFields.DATE_TO)
                const isDateFromAfterDateTo = dayjs(value).isAfter(dayjs(dateToValue))

                // If DATE_FROM is after DATE_TO, adjust DATE_TO to match DATE_FROM
                if (isDateFromAfterDateTo) {
                    formik.setFieldValue(activityFields.DATE_TO, value)
                }
            },
        },
        [activityFields.TIME_FROM]: {
            ...baseTimeInput,
            name: activityFields.TIME_FROM,
            colProps: {
                sm: 5,
            },
            handleChange: (value, formik) => {
                const durationDayValue = getIn(formik.values, activityFields.DURATION_DAY)
                const dateFromValue = getIn(formik.values, activityFields.DATE_FROM)
                const dateTime = `${dateFromValue}T${value}`
                const isDateValid = dayjs(dateTime).isValid()

                if (!durationDayValue && isDateValid) {
                    const timeTo = dayjs(dateTime).add(15, 'minute').format('HH:mm')
                    formik.setFieldValue(activityFields.TIME_TO, timeTo)
                }
            },
            handleInputDisable: (formik) => {
                const durationDayValue = getIn(formik?.values, activityFields.DURATION_DAY)
                return Boolean(durationDayValue)
            },
        },
        [activityFields.DATE_TO]: {
            ...baseDateInput,
            name: activityFields.DATE_TO,
            colProps: {
                sm: 7,
                className: 'pe-0',
            },
        },
        [activityFields.TIME_TO]: {
            ...baseTimeInput,
            name: activityFields.TIME_TO,
            colProps: {
                sm: 5,
            },
            handleInputDisable: (formik) => {
                const durationDayValue = getIn(formik?.values, activityFields.DURATION_DAY)
                return Boolean(durationDayValue)
            },
        },
        [activityFields.DURATION_DAY]: {
            ...baseCheckboxInput,
            name: activityFields.DURATION_DAY,
            label: i18n.t('modal.activity.field.duration'),
            colProps: {
                sm: 6,
                className: 'd-flex align-items-center',
            },
            handleChange: (value, formik) => {
                if (value) {
                    // Checked: Set to whole day times
                    formik.setFieldValue(activityFields.TIME_FROM, '08:00')
                    formik.setFieldValue(activityFields.TIME_TO, '20:00')
                } else {
                    // Unchecked: Revert to current time
                    const now = dayjs()
                    const timeTo = now.add(15, 'minute')
                    formik.setFieldValue(activityFields.TIME_FROM, now.format('HH:mm'))
                    formik.setFieldValue(activityFields.TIME_TO, timeTo.format('HH:mm'))
                }
            },
        },
        [activityFields.COMPLETED]: {
            ...baseCheckboxInput,
            name: activityFields.COMPLETED,
            label: i18n.t('modal.activity.field.completed'),
            colProps: {
                sm: 6,
                className: 'd-flex align-items-center',
            },
            inputProps: {
                className: 'activity-status-check-input',
            },
        },
        [activityFields.ACTIVITY_LOCATION]: {
            ...baseTextInput,
            name: activityFields.ACTIVITY_LOCATION,
            label: i18n.t('app.common.location'),
            colProps: {
                sm: 12,
            },
        },
        [activityFields.CONTACT_COMPANY]: {
            type: 'contact-company-cascade',
            name: activityFields.CONTACT_COMPANY,
            inputs: [
                {
                    name: activityFields.CONTACT_ID,
                },
                {
                    name: activityFields.CONTACT_COMPANY_COLLECTION,
                },
            ],
        },
        [activityFields.PROPERTY_ID]: {
            ...baseAsyncSelectInput,
            name: activityFields.PROPERTY_ID,
            label: i18n.t('app.common.propertyId'),
            searchCriteria: ['internalAppId'],
            noLimit: true,
            isMulti: true,
            colProps: {
                sm: 6,
            },
            useEntityHook: properties.GetProperties.useQuery,
            formatOption: formatPropertyAsyncOption,
            handleInputDisable: (formik) => {
                const projectIdValue = getIn(formik?.values, activityFields.PROJECT_ID)
                return projectIdValue && projectIdValue.length > 0
            },
        },
        [activityFields.PROJECT_ID]: {
            ...baseAsyncSelectInput,
            name: activityFields.PROJECT_ID,
            label: i18n.t('app.common.project.label'),
            searchCriteria: ['search'],
            noLimit: true,
            isMulti: true,
            colProps: {
                sm: 6,
            },
            useEntityHook: projects.GetProjects.useQuery,
            formatOption: formatProjectAsyncOption,
            handleInputDisable: (formik) => {
                const propertyIdValue = getIn(formik?.values, activityFields.PROPERTY_ID)
                return propertyIdValue && propertyIdValue.length > 0
            },
        },
        [activityFields.AGENT_ID]: {
            ...baseSelectInput,
            name: activityFields.AGENT_ID,
            label: i18n.t('app.common.agent'),
            placeholder: i18n.t('app.common.contact.nameAndSurname'),
            required: true,
            colProps: {
                sm: 8,
            },
        },
        [activityFields.NOTIFICATION_SEND]: {
            ...baseCheckboxInput,
            name: activityFields.NOTIFICATION_SEND,
            label: i18n.t('modal.activity.field.notificationSend'),
            colProps: {
                sm: 4,
            },
        },
        [activityFields.REMINDER_CHANNEL]: {
            ...baseSelectInput,
            name: activityFields.REMINDER_CHANNEL,
            label: i18n.t('modal.activity.field.channel'),
            colProps: {
                sm: 6,
            },
        },
        [activityFields.REMINDER_MINUTES_BEFORE]: {
            ...baseSelectInput,
            name: activityFields.REMINDER_MINUTES_BEFORE,
            label: i18n.t('app.common.reminderTime'),
            colProps: {
                sm: 6,
            },
        },
        [activityFields.NOTES]: {
            ...baseTextAreaInput,
            name: activityFields.NOTES,
            label: i18n.t('app.common.comment'),
            colProps: {
                sm: 12,
            },
            inputProps: {
                style: { resize: 'none', height: '90px' },
            },
        },
    }
}
